<template>
  <v-container fluid>
    <v-row v-if="isEditing">
      <v-col cols="12" md="6">
        <v-card class="mb-4">
          <v-card-title>
            <v-btn icon class="mr-2" title="Saved emails" @click="openEmail(null)"><v-icon>mdi-arrow-left</v-icon></v-btn>
            {{ broadcastId ? (savedSubject || '(no subject)') : 'New email' }}
            <v-chip v-if="broadcast" small class="ml-2" :color="statusColors[broadcast.status]" text-color="white">
              {{ broadcast.status }}
            </v-chip>
            <v-chip v-if="dirty && editable" small outlined class="ml-2">unsaved</v-chip>
            <v-spacer/>
            <v-btn text @click="openEmail('new')"><v-icon left>mdi-plus</v-icon>New</v-btn>
            <v-btn v-if="editable && !readOnly" text color="primary" :loading="saving" :disabled="!dirty"
                   @click="save">
              <v-icon left>mdi-content-save</v-icon>Save
            </v-btn>
            <v-btn v-if="broadcast && !readOnly" text :loading="copying" @click="copy(broadcast)">
              <v-icon left>mdi-content-copy</v-icon>Copy
            </v-btn>
            <v-btn v-if="broadcast && broadcast.editable && !readOnly" text color="error" @click="remove(broadcast)">
              <v-icon left>mdi-delete</v-icon>Delete
            </v-btn>
            <v-btn v-if="broadcast && isCancellable(broadcast) && !readOnly" text color="error"
                   @click="cancel(broadcast)">
              <v-icon left>mdi-cancel</v-icon>Cancel send
            </v-btn>
          </v-card-title>
          <v-card-subtitle v-if="broadcast">
            <span v-if="broadcast.copiedFrom">Copied from an earlier email. </span>
            <span v-if="broadcast.queuedAt">
              <span v-if="broadcast.coupon">Coupon <code>{{ broadcast.coupon }}</code>: {{ couponCountsText }}. </span>
              Queued {{ formatTime(broadcast.queuedAt) }} by {{ broadcast.sentBy }},
              sending from {{ formatTime(broadcast.sendAt) }}.
              {{ broadcast.recipientCount - broadcast.remaining }} of {{ broadcast.recipientCount }} sent.
              It can't be changed now: copy it to make a new version.
            </span>
            <span v-else-if="isApplyingCoupon(broadcast)">
              Adding coupon <code>{{ broadcast.coupon }}</code> to accounts:
              {{ couponProgress.done }} of {{ couponProgress.total }} done. The emails queue once that finishes.
            </span>
            <span v-else>Saved {{ formatTime(broadcast.updatedAt) }} by {{ broadcast.updatedBy }}.</span>
            <span v-if="broadcast.cancelledAt">Cancelled {{ formatTime(broadcast.cancelledAt) }}.</span>
          </v-card-subtitle>
          <v-alert v-if="saveError" type="error" text class="mx-4">{{ saveError }}</v-alert>
          <v-alert v-if="broadcast && broadcast.couponError" type="error" text class="mx-4">
            {{ broadcast.couponError }}
          </v-alert>
        </v-card>

        <v-card class="mb-4">
          <v-tabs v-model="tab" grow>
            <v-tab>Recipients</v-tab>
            <v-tab>Email</v-tab>
            <v-tab>Schedule send</v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-radio-group v-model="recipientMode" row class="px-4 pt-2" hide-details :disabled="locked">
                <v-radio label="Choose by filters" value="filters"/>
                <v-radio label="Paste a list of email addresses" value="emails"/>
              </v-radio-group>
              <template v-if="recipientMode === 'filters'">
                <v-card-subtitle class="pt-4">Customers matching every filter you set.</v-card-subtitle>
                <v-card-text>
                  <v-checkbox
                    v-model="filters.includeSubAccounts"
                    label="Include sub-accounts"
                    hide-details
                    class="mt-0 mb-3"
                    :disabled="locked"
                  />

                  <v-menu v-model="showDatePicker" :close-on-content-click="false" offset-y min-width="auto"
                          :disabled="locked">
                    <template v-slot:activator="{on, attrs}">
                      <v-text-field
                        :value="deliveryDatesText"
                        label="Has a delivery on"
                        hint="Pick one or more dates: a delivery on any of them counts"
                        prepend-icon="mdi-calendar"
                        readonly
                        clearable
                        :disabled="locked"
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="filters.deliveryDates = []"
                      />
                    </template>
                    <v-date-picker v-model="filters.deliveryDates" multiple no-title/>
                  </v-menu>
                  <div v-if="filters.deliveryDates.length" class="ml-8 mb-2">
                    <v-chip-group v-model="filters.deliveryTypes" multiple column>
                      <v-chip v-for="type in deliveryTypes" :key="type.value" :value="type.value" filter outlined
                              :disabled="locked">
                        {{ type.text }}
                      </v-chip>
                    </v-chip-group>
                    <div class="text-caption">
                      {{ filters.deliveryTypes.length ? '' : 'No delivery type chosen: every kind of delivery on these dates.' }}
                    </div>
                  </div>

                  <v-autocomplete
                    v-model="filters.areas"
                    :items="areas"
                    label="In area"
                    :hint="filters.deliveryDates.length
                      ? 'The area of their delivery on the chosen dates'
                      : 'The area of their most recent delivery'"
                    persistent-hint
                    prepend-icon="mdi-map-marker-outline"
                    multiple
                    chips
                    small-chips
                    :deletable-chips="!locked"
                    clearable
                    :disabled="locked"
                  />

                  <v-text-field
                    v-model.number="filters.notOrderedWeeks"
                    label="No delivery for at least (weeks)"
                    hint="Customers who have ordered before, but have had no delivery in this many weeks"
                    type="number"
                    min="1"
                    prepend-icon="mdi-calendar-remove"
                    clearable
                    :disabled="locked"
                  />

                  <v-text-field
                    v-model="filters.lastDeliveryAfter"
                    label="Last delivery on or after"
                    hint="Leaves out customers whose last delivery was before this date"
                    type="date"
                    prepend-icon="mdi-calendar-start"
                    clearable
                    :disabled="locked"
                  />

                  <v-autocomplete
                    v-model="filters.plans"
                    :items="planItems"
                    label="On plan"
                    prepend-icon="mdi-clipboard-list-outline"
                    multiple
                    chips
                    small-chips
                    :deletable-chips="!locked"
                    clearable
                    :disabled="locked"
                  />
                </v-card-text>
              </template>
              <v-card-text v-else>
                <v-textarea
                  v-model="emailList"
                  label="Email addresses"
                  hint="One per line, or separated by commas or spaces. Each must be a customer's account address."
                  persistent-hint
                  auto-grow
                  rows="6"
                  :disabled="locked"
                />
                <div class="text-caption mt-2">
                  {{ parsedEmails.length }} address{{ parsedEmails.length === 1 ? '' : 'es' }}
                </div>
              </v-card-text>

              <template v-if="couponResults && couponResults.length">
                <v-card-actions>
                  <span class="ml-2 text-subtitle-1">Coupon on {{ couponResults.length }} accounts</span>
                </v-card-actions>
                <v-data-table :headers="couponResultHeaders" :items="couponResults" item-key="uid" dense
                              :items-per-page="10"/>
              </template>
              <template v-if="sentRecipients">
                <v-card-actions>
                  <span class="ml-2 text-subtitle-1">Sent to {{ sentRecipients.length }} customers</span>
                </v-card-actions>
                <v-text-field v-model="recipientSearch" label="Search" dense clearable class="mx-4"/>
                <v-data-table :headers="sentRecipientHeaders" :items="sentRecipients" :search="recipientSearch"
                              item-key="uid" dense :items-per-page="10">
                  <template v-slot:item.queuedAt="{item}">{{ formatTime(item.queuedAt) }}</template>
                  <template v-slot:item.scheduledFor="{item}">{{ formatTime(item.scheduledFor) }}</template>
                </v-data-table>
              </template>
              <template v-else>
                <v-card-actions>
                  <v-btn color="primary" :disabled="!hasAnyFilter" :loading="findingRecipients" @click="findRecipients">
                    Find customers
                  </v-btn>
                  <span v-if="recipients" class="ml-4 text-subtitle-1">
                    {{ recipients.length }} customer{{ recipients.length === 1 ? '' : 's' }}
                  </span>
                  <v-spacer/>
                  <v-btn v-if="recipients && recipients.length" text @click="showRecipients = !showRecipients">
                    {{ showRecipients ? 'Hide' : 'Show' }} list
                  </v-btn>
                </v-card-actions>
                <v-alert v-if="recipientsError" type="error" text class="mx-4">{{ recipientsError }}</v-alert>
                <v-alert v-if="unmatched.length" type="warning" text class="mx-4">
                  {{ unmatched.length }} address{{ unmatched.length === 1 ? ' is not a customer' : 'es are not customers' }}
                  who can be emailed, so won't be sent to: {{ unmatched.join(', ') }}
                </v-alert>
                <v-expand-transition>
                  <div v-if="showRecipients && recipients">
                    <v-text-field v-model="recipientSearch" label="Search" dense clearable class="mx-4"/>
                    <v-data-table :headers="recipientHeaders" :items="recipients" :search="recipientSearch"
                                  item-key="uid" dense :items-per-page="10"/>
                  </div>
                </v-expand-transition>

                <template v-if="canUseCoupons">
                  <v-divider class="mt-2"/>
                  <v-card-text>
                    <v-row align="center" dense>
                      <v-col cols="12" sm="8">
                        <v-text-field
                          v-model="coupon"
                          label="Add a coupon to each customer's account (optional)"
                          placeholder="FED10"
                          prepend-icon="mdi-ticket-percent-outline"
                          clearable
                          hide-details
                          :disabled="locked"
                        />
                      </v-col>
                      <v-col cols="12" sm="4">
                        <v-btn outlined block :disabled="!coupon || !recipients || !recipients.length || locked"
                               :loading="checkingCoupon" @click="checkCoupon">
                          Check coupon
                        </v-btn>
                      </v-col>
                    </v-row>
                    <div v-if="checkingCoupon && couponCheckProgress" class="text-caption mt-2">
                      {{ couponCheckProgress }}
                    </div>
                    <v-alert v-if="couponError" type="error" text dense class="mt-3">{{ couponError }}</v-alert>

                    <template v-if="couponCheck">
                      <div class="mt-3">
                        <code>{{ couponCheck.coupon.id }}</code>
                        <span v-if="couponCheck.coupon.name"> - {{ couponCheck.coupon.name }}</span>
                        <span class="ml-2">{{ couponValueText(couponCheck.coupon) }}, {{ couponCheck.coupon.duration }}</span>
                        <div v-if="couponCheck.checkedAt" class="text-caption">
                          Checked {{ formatTime(couponCheck.checkedAt) }}
                        </div>
                      </div>
                      <v-alert v-for="note in couponCheck.notes" :key="note" type="info" text dense class="mt-2 mb-0">
                        {{ note }}
                      </v-alert>
                      <div class="mt-2">
                        {{ couponTotals.ready }} will get it<span v-if="couponTotals.hasThis">,
                        {{ couponTotals.hasThis }} already have it</span>.
                      </div>
                      <v-alert v-if="couponOverLimit" type="error" text dense class="mt-2">{{ couponOverLimit }}</v-alert>

                      <template v-if="couponChoiceGroups.length">
                        <div class="mt-4 d-flex align-center flex-wrap">
                          <b>Choose for these customers</b>
                          <v-spacer/>
                          <v-btn small text @click="decideAll('email')">Email all, no coupon</v-btn>
                          <v-btn small text @click="decideAll('remove')">Remove all</v-btn>
                        </div>
                        <div class="text-caption mb-2">
                          Email, no coupon: they get this email and their account is left as it is, so check the message
                          makes sense without it. Remove: they don't get this email. Replace: their coupon comes off and
                          they lose it.
                        </div>
                        <v-expansion-panels accordion multiple>
                          <v-expansion-panel v-for="group in couponChoiceGroups" :key="group.key">
                            <v-expansion-panel-header>
                              <div>
                                <template v-if="group.coupon">
                                  <code>{{ group.coupon.id }}</code>
                                  <span v-if="group.coupon.name"> - {{ group.coupon.name }}</span>
                                  <span class="text-caption ml-1">{{ couponValueText(group.coupon) }}, {{ group.coupon.duration }}</span>
                                </template>
                                <template v-else>{{ group.title }}</template>
                                <v-chip v-if="isAllRemoved(group)" x-small color="error" text-color="white" class="ml-2">
                                  All removed
                                </v-chip>
                                <div class="text-caption">
                                  {{ group.rows.length }} {{ group.rows.length === 1 ? 'customer' : 'customers' }}:
                                  {{ groupSummary(group) }}
                                </div>
                              </div>
                              <template v-slot:actions>
                                <v-btn v-for="choice in group.choices" :key="choice.value" small text
                                       @click.stop="decideGroup(group, choice.value)">
                                  {{ choice.text }}
                                </v-btn>
                              </template>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div v-for="row in group.rows" :key="row.uid" class="d-flex align-center flex-wrap py-1">
                                <div>
                                  {{ row.email }} <span class="text-caption">{{ row.uid }}</span>
                                  <div class="text-caption">
                                    {{ row.stripe_id }}
                                    <span v-if="row.main_account">sub-account of {{ row.main_account }}</span>
                                  </div>
                                </div>
                                <v-spacer/>
                                <v-btn-toggle :value="couponDecisions[row.uid]" dense
                                              @change="$set(couponDecisions, row.uid, $event)">
                                  <v-btn v-for="choice in group.choices" :key="choice.value" small :value="choice.value">
                                    {{ choice.text }}
                                  </v-btn>
                                </v-btn-toggle>
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </template>
                    </template>
                  </v-card-text>
                </template>
              </template>
            </v-tab-item>
            <v-tab-item>
              <v-card-text>
                <v-text-field v-model="from" label="From" :disabled="locked" :rules="[fromRule]"
                              hint="Name and address customers see, eg. Fed <support@fedfedfed.com>"/>
                <v-select
                  v-model="templateId"
                  :items="templateItems"
                  label="Email template"
                  placeholder="Plain email"
                  :hint="templateId ? templateHint : ''"
                  persistent-hint
                  clearable
                  :disabled="locked"
                  class="mb-3"
                />
                <template v-if="!templateId">
                  <v-text-field v-model="subject" label="Subject" :disabled="locked"/>
                  <v-textarea
                    v-model="body"
                    label="Message"
                    hint="Leave a blank line to start a new paragraph. Web and email addresses become links."
                    persistent-hint
                    auto-grow
                    rows="12"
                    :disabled="locked"
                  />
                </template>
              </v-card-text>
            </v-tab-item>
            <v-tab-item>
              <v-card-text>
                <v-row align="center" dense>
                  <v-col cols="12" sm="8">
                    <v-text-field v-model="testEmailAddress" label="Send a test to" prepend-icon="mdi-email-outline"
                                  :disabled="readOnly"/>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-btn outlined block :disabled="!canSendTest" :loading="sendingTest" @click="sendTest">
                      Send test
                    </v-btn>
                  </v-col>
                </v-row>
                <v-alert v-if="testResult" :type="testResult.type" text dense>{{ testResult.text }}</v-alert>

                <template v-if="editable">
                  <v-divider class="my-4"/>
                  <v-radio-group v-model="sendLater" row :disabled="readOnly">
                    <v-radio label="Send now" :value="false"/>
                    <v-radio label="Send later" :value="true"/>
                  </v-radio-group>
                  <v-text-field
                    v-if="sendLater"
                    v-model="sendAtLocal"
                    label="Send at (your local time)"
                    type="datetime-local"
                    prepend-icon="mdi-clock-outline"
                  />
                  <div v-if="recipients && sendCount > recipientsPerMinute" class="text-caption mb-2">
                    Emails go out {{ recipientsPerMinute }} a minute, so the last one is sent about
                    {{ Math.ceil(sendCount / recipientsPerMinute) }} minutes after the first.
                  </div>
                  <v-btn color="primary" large :disabled="!canSend" @click="showConfirm = true">
                    <v-icon left>mdi-send</v-icon>
                    Send to {{ recipients ? sendCount : '…' }} customers
                  </v-btn>
                  <div v-if="sendBlockedReason" class="text-caption mt-2">{{ sendBlockedReason }}</div>
                </template>
                <v-alert v-if="sendResult" :type="sendResult.type" text class="mt-4">{{ sendResult.text }}</v-alert>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="preview-card">
          <v-card-title>
            Preview
            <v-spacer/>
            <v-btn-toggle v-model="viewportMode" mandatory dense>
              <v-btn value="desktop" small><v-icon small>mdi-monitor</v-icon></v-btn>
              <v-btn value="mobile" small><v-icon small>mdi-cellphone</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>
            <b>From:</b> {{ from }}<br/>
            <b>Subject:</b> {{ shownSubject || '(no subject)' }}
            <v-progress-circular v-if="previewing" indeterminate size="14" width="2" class="ml-2"/>
          </v-card-subtitle>
          <v-autocomplete
            v-if="templateId"
            v-model="previewUid"
            :items="previewCustomers"
            label="Preview as"
            :placeholder="previewCustomers.length ? 'Sample customer' : 'Sample customer (find customers to choose one)'"
            persistent-placeholder
            hint="Also fills in the test email, which still goes only to the test address."
            persistent-hint
            clearable
            dense
            class="mx-4 mb-2"
          />
          <v-alert v-if="previewError" type="error" text class="mx-4">{{ previewError }}</v-alert>
          <div class="preview-container">
            <div :class="viewportMode === 'mobile' ? 'mobile-viewport' : 'desktop-viewport'">
              <iframe ref="previewFrame" :srcdoc="previewHtml" frameborder="0" @load="fitPreview"/>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-else>
      <v-card-title>
        Saved emails
        <v-spacer/>
        <v-btn icon :loading="loadingBroadcasts" @click="loadBroadcasts"><v-icon>mdi-refresh</v-icon></v-btn>
        <v-btn v-if="!readOnly" color="primary" class="ml-2" @click="openEmail('new')">
          <v-icon left>mdi-plus</v-icon>New email
        </v-btn>
      </v-card-title>
      <v-data-table :headers="broadcastHeaders" :items="broadcasts" :loading="loadingBroadcasts"
                    sort-by="updatedAt" sort-desc class="clickable-rows" @click:row="row => openEmail(row.id)">
        <template v-slot:item.subject="{item}">{{ item.subject || '(no subject)' }}</template>
        <template v-slot:item.filters="{item}">
          <div v-for="line in describeFilters(item.filters)" :key="line">{{ line }}</div>
        </template>
        <template v-slot:item.status="{item}">
          <v-chip small :color="statusColors[item.status]" text-color="white">{{ item.status }}</v-chip>
        </template>
        <template v-slot:item.updatedAt="{item}">
          {{ formatTime(item.updatedAt) }}
          <div class="text-caption">{{ item.updatedBy }}</div>
        </template>
        <template v-slot:item.queuedAt="{item}">
          {{ formatTime(item.queuedAt) }}
          <div class="text-caption">{{ item.sentBy }}</div>
        </template>
        <template v-slot:item.sendAt="{item}">{{ formatTime(item.sendAt) }}</template>
        <template v-slot:item.recipientCount="{item}">
          <span v-if="item.recipientCount">
            <span v-if="item.remaining">{{ item.recipientCount - item.remaining }} / </span>{{ item.recipientCount }}
          </span>
        </template>
        <template v-slot:item.actions="{item}">
          <v-btn v-if="!readOnly" small text @click.stop="copy(item)">Copy</v-btn>
          <v-btn v-if="!readOnly && item.editable" small text color="error" @click.stop="remove(item)">Delete</v-btn>
          <v-btn v-if="!readOnly && isCancellable(item)"
                 small text color="error" @click.stop="cancel(item)">
            Cancel
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showConfirm" max-width="500">
      <v-card>
        <v-card-title>Send this email?</v-card-title>
        <v-card-text>
          <p><b>{{ shownSubject }}</b></p>
          <p v-if="templateId">Using the email template <code>{{ templateId }}</code>.</p>
          <p>
            to <b>{{ recipients ? sendCount : 0 }} customers</b>,
            {{ sendLater ? 'starting ' + formatTime(sendAt) : 'starting now' }}.
          </p>
          <p>
            Once sent the email can't be changed; copy it to make a new version. Cancelling stops the emails not yet
            sent, but sent emails can't be taken back.
          </p>
        </v-card-text>
        <v-card-text v-if="couponCheck" class="pb-0">
          <p>
            Coupon <code>{{ couponCheck.coupon.id }}</code> goes on the accounts <b>as soon as you confirm</b>, even
            for a later send: added to {{ couponTotals.ready }}, replaced on {{ couponTotals.replace }}.
            <span v-if="couponTotals.emailOnly">{{ couponTotals.emailOnly }} get the email without it. </span>
            If adding it fails for someone when you send, they are not emailed. Cancelling leaves coupons already added on the accounts.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showConfirm = false">Back</v-btn>
          <v-btn color="primary" :loading="sending" @click="send">
            Send to {{ recipients ? sendCount : 0 }} customers
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '../api';
import {mapActions, mapGetters, mapState} from 'vuex';
import {hasRole} from '../router';
import {
  couponChoiceGroups, emailOnlyUids, isAllRemoved, isSameCustomers, removedUids, replacements, undecidedCount,
} from '../utils/broadcastCoupon';
import {formatCurrency} from '@/store/utils';

// keep in step with RECIPIENTS_PER_MINUTE in fed-nutrition-api's emailBroadcastService
const recipientsPerMinute = 50;
// keep in step with DEFAULT_FROM in emailBroadcastService
const defaultFrom = 'Fed <support@fedfedfed.com>';
const fromPattern = /^([^<>@]+<\s*[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+\s*>|[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+)$/;

const customersChangedText = 'The customers have changed since the coupon was checked, so check it again.';

// templates that can't be picked: the plain email itself, and the shared styles
const hiddenTemplates = ['broadcast', 'global-styles'];
const templateHint = 'The subject and message come from the template, which can use {{firstName}} and {{lastName}}. ' +
  'Nothing else is filled in, so check the preview.';

// anything that looks like an address, so 'Jo <jo@x.com>' and comma lists paste in as is
const emailPattern = /[^\s<>,;"']+@[^\s<>,;"']+\.[^\s<>,;"']+/g;

function emptyFilters() {
  return {
    deliveryDates: [], deliveryTypes: ['home'], notOrderedWeeks: null, lastDeliveryAfter: null, plans: [], areas: [],
    includeSubAccounts: false,
  };
}

export default {
  name: 'EmailBroadcast',
  data() {
    return {
      // the saved email being shown; null for a new, unsaved one
      broadcastId: null,
      broadcast: null,
      // 0 recipients, 1 email, 2 schedule send
      tab: 0,
      savedState: null,
      saving: false,
      saveError: null,
      copying: false,
      filters: emptyFilters(),
      deliveryTypes: [
        {value: 'home', text: 'Home delivery'},
        {value: 'office', text: 'Office'},
        {value: 'pickup', text: 'Pickup'},
        {value: 'shipping', text: 'Shipping'},
      ],
      showDatePicker: false,
      recipients: null,
      recipientsError: null,
      findingRecipients: false,
      showRecipients: false,
      // 'filters', or 'emails' to send to a pasted list of addresses instead
      recipientMode: 'filters',
      // the code to add to each account, and the check of it against the customers found
      coupon: '',
      couponCheck: null,
      // the check being polled; clearing it abandons the poll
      couponCheckId: null,
      checkingCoupon: false,
      couponCheckProgress: '',
      couponError: null,
      // uid -> 'replace', 'email' (no coupon) or 'remove'; see utils/broadcastCoupon
      couponDecisions: {},
      couponResults: null,
      couponResultHeaders: [
        {text: 'Email', value: 'email'},
        {text: 'Uid', value: 'uid'},
        {text: 'Stripe customer', value: 'stripe_id'},
        {text: 'Main account', value: 'main_account'},
        {text: 'Outcome', value: 'outcome'},
        {text: 'Coupon before', value: 'previous_coupon'},
        {text: 'Error', value: 'error'},
      ],
      // shipping groups for the area filter
      areas: [],
      emailList: '',
      unmatched: [],
      sentRecipients: null,
      recipientSearch: '',
      recipientHeaders: [
        {text: 'Email', value: 'email'},
        {text: 'Name', value: 'name'},
        {text: 'Plan', value: 'plan'},
      ],
      sentRecipientHeaders: [
        {text: 'Email', value: 'email'},
        {text: 'Uid', value: 'uid'},
        {text: 'Queued', value: 'queuedAt'},
        {text: 'Scheduled for', value: 'scheduledFor'},
      ],
      from: defaultFrom,
      subject: '',
      body: '',
      // an email template from the template editor; '' for the plain email
      templateId: '',
      templates: [],
      templateHint,
      previewSubject: '',
      // the chosen customer whose details fill in the preview; null for the sample one
      previewUid: null,
      previewHtml: '',
      previewError: null,
      previewing: false,
      previewTimer: null,
      viewportMode: 'desktop',
      testEmailAddress: '',
      sendingTest: false,
      testResult: null,
      sendLater: false,
      sendAtLocal: '',
      showConfirm: false,
      sending: false,
      sendResult: null,
      recipientsPerMinute,
      broadcasts: [],
      loadingBroadcasts: false,
      refreshTimer: null,
      broadcastHeaders: [
        {text: 'Subject', value: 'subject'},
        {text: 'Sent to', value: 'filters', sortable: false},
        {text: 'Status', value: 'status'},
        {text: 'Edited', value: 'updatedAt'},
        {text: 'Sent', value: 'queuedAt'},
        {text: 'Scheduled for', value: 'sendAt'},
        {text: 'Customers', value: 'recipientCount'},
        {text: '', value: 'actions', sortable: false, align: 'end'},
      ],
      statusColors: {
        draft: 'blue-grey', copied: 'blue-grey', scheduled: 'blue', sending: 'orange', sent: 'green',
        'applying coupon': 'orange',
        cancelled: 'grey darken-1',
      },
    };
  },
  computed: {
    ...mapState(['plans', 'user']),
    ...mapGetters(['getCurrentUser']),
    canUseCoupons() {
      return hasRole(this.getCurrentUser, 'CustomerCreditAndCoupons');
    },
    readOnly() {
      return !!this.$route.meta.readOnly;
    },
    // ?id=new or ?id=<saved email> shows the editor; no id shows the saved emails
    isEditing() {
      return !!this.$route.query.id;
    },
    // a new email, a draft or a copy; sent and cancelled emails are fixed
    editable() {
      return !this.broadcast || this.broadcast.editable;
    },
    locked() {
      return this.readOnly || !this.editable;
    },
    savedSubject() {
      return this.broadcast ? this.broadcast.subject : '';
    },
    // either a template or a typed subject and message, never both
    emailContent() {
      return this.templateId
        ? {templateId: this.templateId, subject: '', body: ''}
        : {templateId: null, subject: this.subject, body: this.body};
    },
    currentState() {
      return JSON.stringify({
        ...this.emailContent, from: this.from, filters: this.requestFilters, coupon: this.coupon || '',
        couponDecisions: this.couponDecisions,
      });
    },
    previewCustomers() {
      return (this.recipients || this.sentRecipients || [])
        .map(({uid, email, name}) => ({value: uid, text: name ? `${name} <${email}>` : email}));
    },
    templateItems() {
      return this.templates
        .filter(({id}) => !hiddenTemplates.includes(id))
        .map(({id, description}) => ({value: id, text: description ? `${id} - ${description}` : id}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    // a template's own subject line decides what customers see
    shownSubject() {
      return this.templateId ? this.previewSubject : this.subject;
    },
    dirty() {
      return this.currentState !== this.savedState;
    },
    planItems() {
      return Object.entries(this.plans || {})
        .map(([id, plan]) => ({value: id, text: plan.name ? `${plan.name} (${id})` : id}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    deliveryDatesText() {
      return [...this.filters.deliveryDates].sort().map(date => this.formatDate(date)).join(', ');
    },
    parsedEmails() {
      return [...new Set((this.emailList.match(emailPattern) || []).map(email => email.toLowerCase()))];
    },
    requestFilters() {
      if (this.recipientMode === 'emails') {
        return this.parsedEmails.length ? {emails: this.parsedEmails} : {};
      }
      const {deliveryDates, deliveryTypes, notOrderedWeeks, lastDeliveryAfter, plans, areas, includeSubAccounts} = this.filters;
      const filters = {};
      if (deliveryDates.length) {
        filters.deliveryDates = [...deliveryDates].sort();
        filters.deliveryTypes = deliveryTypes;
      }
      if (notOrderedWeeks > 0) {
        filters.notOrderedWeeks = notOrderedWeeks;
      }
      if (lastDeliveryAfter) {
        filters.lastDeliveryAfter = lastDeliveryAfter;
      }
      if (plans.length) {
        filters.plans = plans;
      }
      if (areas.length) {
        filters.areas = areas;
      }
      if (includeSubAccounts) {
        filters.includeSubAccounts = true;
      }
      return filters;
    },
    hasAnyFilter() {
      const {deliveryDates, notOrderedWeeks, lastDeliveryAfter, plans, areas, emails} = this.requestFilters;
      return !!(deliveryDates || notOrderedWeeks || lastDeliveryAfter || plans || areas || emails);
    },
    hasMessage() {
      const hasContent = this.templateId || (this.subject.trim() && this.body.trim());
      return !!hasContent && this.fromRule(this.from) === true;
    },
    canSendTest() {
      return !this.readOnly && this.hasMessage && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.testEmailAddress);
    },
    couponRows() {
      return this.couponCheck ? this.couponCheck.rows : [];
    },
    couponChoiceGroups() {
      return couponChoiceGroups(this.couponRows);
    },
    couponRemovedUids() {
      return removedUids(this.couponRows, this.couponDecisions);
    },
    // who the email goes to once the coupon check has taken people off
    sendCount() {
      return (this.recipients || []).length - this.couponRemovedUids.length;
    },
    couponTotals() {
      return {
        ready: this.couponRows.filter(row => row.status === 'ready' && !['email', 'remove'].includes(this.couponDecisions[row.uid])).length,
        emailOnly: emailOnlyUids(this.couponRows, this.couponDecisions).length,
        hasThis: this.couponRows.filter(row => row.status === 'has this coupon').length,
        replace: replacements(this.couponRows, this.couponDecisions).length,
      };
    },
    // Stripe refuses a coupon past its redemption limit
    couponOverLimit() {
      const {max_redemptions, times_redeemed} = (this.couponCheck && this.couponCheck.coupon) || {};
      if (max_redemptions == null) return null;
      const left = max_redemptions - times_redeemed;
      const needed = this.couponTotals.ready + this.couponTotals.replace;
      return needed > left ? `This coupon has ${left} redemptions left, and ${needed} accounts need one.` : null;
    },
    couponProgress() {
      return (this.broadcast && this.broadcast.couponProgress) || {done: 0, total: 0};
    },
    couponCountsText() {
      const counts = (this.broadcast && this.broadcast.couponCounts) || {};
      const {applied = 0, replaced = 0, already = 0, skipped = 0, failed = 0} = counts;
      return `added to ${applied}, replaced on ${replaced}, already on ${already}, ` +
        `${counts['email only'] || 0} emailed without it, skipped ${skipped}, failed ${failed}`;
    },
    sendAt() {
      return this.sendLater && this.sendAtLocal ? new Date(this.sendAtLocal).getTime() : null;
    },
    sendBlockedReason() {
      if (this.readOnly) return 'You have read-only access to this page.';
      if (!this.recipients) return 'Find customers first.';
      if (!this.recipients.length) return 'No customers match the filters.';
      if (this.coupon && !this.couponCheck) return 'Check the coupon first (Recipients tab).';
      if (this.coupon && undecidedCount(this.couponRows, this.couponDecisions)) {
        return 'Choose what happens to each customer listed under the coupon (Recipients tab).';
      }
      if (this.couponOverLimit) return this.couponOverLimit;
      if (!this.sendCount) return 'The coupon check removed every customer.';
      if (!this.hasMessage) return 'Choose a sender, and a template or a subject and message.';
      if (this.sendLater && !this.sendAt) return 'Choose when to send.';
      if (this.sendLater && this.sendAt < Date.now()) return 'The send time is in the past.';
      return null;
    },
    canSend() {
      return !this.sendBlockedReason;
    },
  },
  watch: {
    requestFilters() {
      // a list found with other filters must not be sent to
      this.recipients = null;
      this.unmatched = [];
      this.recipientsError = null;
      this.showRecipients = false;
      this.clearCouponCheck();
    },
    coupon() {
      this.clearCouponCheck();
    },
    // finding the same customers again keeps the check and the choices made from it
    recipients(recipients) {
      if (!this.couponCheck || (recipients && isSameCustomers(this.couponRows, recipients))) return;
      this.clearCouponCheck();
      if (recipients) {
        this.couponError = customersChangedText;
      }
    },
    subject() {
      this.schedulePreview();
    },
    body() {
      this.schedulePreview();
    },
    templateId() {
      this.schedulePreview();
    },
    previewUid() {
      this.schedulePreview();
    },
    previewCustomers(customers) {
      if (this.previewUid && !customers.some(({value}) => value === this.previewUid)) {
        this.previewUid = null;
      }
    },
    '$route.query.id'(id) {
      if (!id) {
        this.showEmail(null);
        this.loadBroadcasts();
      } else if (id === 'new') {
        this.showEmail(null);
      } else if (id !== this.broadcastId) {
        this.loadEmail(id);
      }
    },
  },
  async mounted() {
    this.testEmailAddress = (this.user && this.user.email) || '';
    const id = this.$route.query.id;
    this.loadEmail(id && id !== 'new' ? id : null);
    this.loadBroadcasts();
    this.refreshTimer = setInterval(() => {
      if (this.broadcasts.some(b => this.isCancellable(b))) {
        this.loadBroadcasts();
        if (this.broadcast && !this.broadcast.editable) {
          this.loadEmail(this.broadcastId);
        }
      }
    }, 30 * 1000);
    this.loadAreas();
    this.loadTemplates();
    await this.fetchPlans();
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    clearTimeout(this.previewTimer);
  },
  methods: {
    ...mapActions(['fetchPlans']),
    errorText(error, fallback) {
      return (error.response && error.response.data && error.response.data.error) || fallback;
    },
    fromRule(value) {
      return fromPattern.test((value || '').trim()) || 'Like Fed <support@fedfedfed.com>';
    },
    formatTime(ms) {
      return ms ? new Date(ms).toLocaleString() : '';
    },
    // id: a saved email, 'new', or null for the list; the $route watch shows it
    openEmail(id) {
      if ((this.$route.query.id || null) === id) return;
      if (this.isEditing && this.dirty && this.editable && !confirm('Discard your unsaved changes?')) return;
      this.$router.push({query: {...this.$route.query, id: id || undefined}});
    },
    formatDate(date) {
      return new Date(`${date}T12:00:00`)
        .toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'});
    },
    // one line per filter, eg. 'Delivery on Sun, Oct 11 (home delivery)'
    describeFilters(filters) {
      const {deliveryDates = [], deliveryTypes, notOrderedWeeks, lastDeliveryAfter, plans, areas, includeSubAccounts, emails} = filters || {};
      if (emails && emails.length) {
        return [`List of ${emails.length} email address${emails.length === 1 ? '' : 'es'}`];
      }
      const lines = [];
      if (deliveryDates.length) {
        const date = deliveryDates.map(this.formatDate).join(' or ');
        const types = (deliveryTypes || [])
          .map(value => this.deliveryTypes.find(type => type.value === value).text.toLowerCase());
        lines.push(`Delivery on ${date}${types.length ? ` (${types.join(', ')})` : ''}`);
      }
      if (notOrderedWeeks) {
        lines.push(`No delivery for ${notOrderedWeeks}+ weeks`);
      }
      if (lastDeliveryAfter) {
        lines.push(`Last delivery on or after ${this.formatDate(lastDeliveryAfter)}`);
      }
      if (plans && plans.length) {
        lines.push(`Plan${plans.length === 1 ? '' : 's'}: ${plans.join(', ')}`);
      }
      if (areas && areas.length) {
        lines.push(`Area${areas.length === 1 ? '' : 's'}: ${areas.join(', ')}`);
      }
      if (includeSubAccounts) {
        lines.push('Including sub-accounts');
      }
      return lines;
    },
    showEmail(broadcast) {
      this.broadcast = broadcast;
      this.broadcastId = broadcast ? broadcast.id : null;
      this.tab = 0;
      this.templateId = (broadcast && broadcast.templateId) || '';
      // a template email's saved subject is the template's, not something typed
      this.subject = broadcast && !this.templateId ? broadcast.subject : '';
      this.body = broadcast && !this.templateId ? broadcast.body : '';
      this.from = (broadcast && broadcast.from) || defaultFrom;
      this.previewUid = null;
      const {emails, ...filters} = (broadcast && broadcast.filters) || {};
      this.filters = {...emptyFilters(), ...filters};
      this.recipientMode = emails ? 'emails' : 'filters';
      this.emailList = (emails || []).join('\n');
      this.coupon = (broadcast && broadcast.coupon) || '';
      this.clearCouponCheck();
      this.couponResults = null;
      this.savedState = this.currentState;
      this.saveError = null;
      this.sendResult = null;
      this.testResult = null;
      this.recipients = null;
      this.sentRecipients = null;
      this.schedulePreview();
    },
    async loadEmail(id) {
      if (!id) {
        this.showEmail(null);
        return;
      }
      try {
        const broadcast = (await api.get(`/v2/email-broadcasts/${id}`)).data;
        const isRefresh = id === this.broadcastId && !this.dirty;
        if (isRefresh) {
          this.broadcast = broadcast;
        } else {
          this.showEmail(broadcast);
          await this.restoreCouponCheck(broadcast);
        }
        if (!broadcast.editable) {
          this.sentRecipients = (await api.get(`/v2/email-broadcasts/${id}/recipients`)).data;
          if (broadcast.coupon) {
            this.couponResults = (await api.get(`/v2/email-broadcasts/${id}/coupon-results`)).data;
          }
        }
      } catch (error) {
        this.saveError = this.errorText(error, 'Failed to load the email');
      }
    },
    // the customers the saved check was made for, with the choices made from them
    async restoreCouponCheck(broadcast) {
      if (!broadcast.editable || !broadcast.coupon || !this.canUseCoupons) return;
      const {id} = broadcast;
      const saved = (await api.get(`/v2/email-broadcasts/${id}/coupon-check`)).data;
      if (!saved || saved.coupon.id !== broadcast.coupon.trim() || id !== this.broadcastId) return;
      // after the filter watcher, which forgets a list found with other filters
      await this.$nextTick();
      const hasOtherChanges = this.dirty;
      this.recipients = saved.rows;
      this.couponCheck = saved;
      this.couponDecisions = {...broadcast.couponDecisions};
      if (!hasOtherChanges) {
        this.savedState = this.currentState;
      }
    },
    async save() {
      this.saving = true;
      this.saveError = null;
      try {
        const payload = {
          ...this.emailContent, from: this.from, filters: this.requestFilters, coupon: this.coupon || null,
          ...(this.canUseCoupons ? {couponDecisions: this.couponDecisions} : {}),
        };
        const response = this.broadcastId
          ? await api.put(`/v2/email-broadcasts/${this.broadcastId}`, payload)
          : await api.post('/v2/email-broadcasts', payload);
        const isNew = !this.broadcastId;
        this.broadcast = response.data;
        this.broadcastId = response.data.id;
        this.savedState = this.currentState;
        if (isNew) {
          this.$router.replace({query: {...this.$route.query, id: this.broadcastId}});
        }
        this.loadBroadcasts();
        return true;
      } catch (error) {
        this.saveError = this.errorText(error, 'Failed to save');
        return false;
      } finally {
        this.saving = false;
      }
    },
    async copy(broadcast) {
      if (this.isEditing && this.dirty && this.editable && !confirm('Discard your unsaved changes?')) return;
      this.copying = true;
      try {
        const copy = (await api.post(`/v2/email-broadcasts/${broadcast.id}/copy`)).data;
        this.showEmail(copy);
        this.$router.push({query: {...this.$route.query, id: copy.id}});
        this.loadBroadcasts();
      } catch (error) {
        this.saveError = this.errorText(error, 'Failed to copy');
      } finally {
        this.copying = false;
      }
    },
    async findRecipients() {
      this.findingRecipients = true;
      this.recipientsError = null;
      this.sendResult = null;
      try {
        const response = await api.post('/v2/email-broadcasts/recipients', this.requestFilters);
        this.recipients = response.data.recipients;
        this.unmatched = response.data.unmatched || [];
      } catch (error) {
        this.recipientsError = this.errorText(error, 'Failed to find customers');
      } finally {
        this.findingRecipients = false;
      }
    },
    clearCouponCheck() {
      this.couponCheck = null;
      this.couponDecisions = {};
      this.couponError = null;
      this.couponCheckId = null;
    },
    // reads each customer's Stripe account on the server, which can take minutes on a long list
    async checkCoupon() {
      this.clearCouponCheck();
      this.checkingCoupon = true;
      this.couponCheckProgress = '';
      const couponCode = this.coupon.trim();
      try {
        // the result is kept on the saved email
        if ((this.dirty || !this.broadcastId) && !await this.save()) return;
        const {id} = (await api.post('/v2/email-broadcasts/coupon-check', {
          id: this.broadcastId, filters: this.requestFilters, coupon: couponCode,
        })).data;
        this.couponCheckId = id;
        while (this.couponCheckId === id) {
          const check = (await api.get(`/v2/email-broadcasts/coupon-check/${id}`)).data;
          if (check.error) throw new Error(check.error);
          if (!check.running) {
            if (this.couponCheckId === id) this.couponCheck = check;
            return;
          }
          this.couponCheckProgress = check.total ? `Read ${check.read} of ${check.total} Stripe customers` : 'Finding customers';
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      } catch (error) {
        this.couponError = this.errorText(error, error.message || 'Failed to check the coupon');
      } finally {
        this.checkingCoupon = false;
      }
    },
    couponValueText(coupon) {
      if (coupon.percent_off != null) return `${coupon.percent_off}% off`;
      return coupon.amount_off != null ? `${formatCurrency(coupon.amount_off)} off` : '';
    },
    isAllRemoved(group) {
      return isAllRemoved(group, this.couponDecisions);
    },
    groupSummary(group) {
      const count = value => group.rows.filter(row => this.couponDecisions[row.uid] === value).length;
      const undecided = count(undefined);
      const chosen = group.choices.map(({value, text}) => `${count(value)} ${text.toLowerCase()}`).join(', ');
      return `${chosen}${undecided ? `, ${undecided} to choose` : ''}`;
    },
    decideGroup({rows}, decision) {
      rows.forEach(row => this.$set(this.couponDecisions, row.uid, decision));
    },
    decideAll(decision) {
      this.couponChoiceGroups.forEach(group => this.decideGroup(group, decision));
    },
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(this.renderPreview, 500);
    },
    async renderPreview() {
      this.previewing = true;
      try {
        const response = await api.post('/v2/email-broadcasts/preview', {...this.emailContent, uid: this.previewUid});
        this.previewHtml = response.data.html || '';
        this.previewSubject = response.data.subject || '';
        this.previewError = null;
      } catch (error) {
        this.previewError = this.errorText(error, 'Failed to render the preview');
      } finally {
        this.previewing = false;
      }
    },
    fitPreview() {
      const frame = this.$refs.previewFrame;
      if (frame && frame.contentDocument && frame.contentDocument.body) {
        frame.style.height = frame.contentDocument.body.scrollHeight + 'px';
      }
    },
    async sendTest() {
      this.sendingTest = true;
      this.testResult = null;
      try {
        const response = await api.post('/v2/email-broadcasts/send-test', {
          ...this.emailContent,
          from: this.from,
          uid: this.previewUid,
          to: this.testEmailAddress,
        });
        this.testResult = {type: 'success', text: `Test sent to ${response.data.to}`};
      } catch (error) {
        this.testResult = {type: 'error', text: this.errorText(error, 'Failed to send the test')};
      } finally {
        this.sendingTest = false;
      }
    },
    // the server sends what is saved, so save first
    async send() {
      this.sending = true;
      this.sendResult = null;
      try {
        if (this.dirty || !this.broadcastId) {
          const saved = await this.save();
          if (!saved) return;
        }
        const response = await api.post(`/v2/email-broadcasts/${this.broadcastId}/send`, {
          uids: this.recipients.map(({uid}) => uid),
          sendAt: this.sendAt,
          coupon: this.couponCheck ? {
            id: this.couponCheck.coupon.id,
            removeUids: this.couponRemovedUids,
            emailOnlyUids: emailOnlyUids(this.couponRows, this.couponDecisions),
            replace: replacements(this.couponRows, this.couponDecisions),
          } : null,
        });
        const {recipientCount, sendAt} = response.data;
        this.sendResult = {
          type: 'success',
          text: this.couponCheck
            ? 'Adding the coupon to the accounts; the emails queue once that finishes.'
            : `Queued for ${recipientCount} customers, sending from ${this.formatTime(sendAt)}.`,
        };
        this.recipients = null;
        await this.loadEmail(this.broadcastId);
        this.loadBroadcasts();
      } catch (error) {
        this.sendResult = {type: 'error', text: this.errorText(error, 'Failed to send')};
      } finally {
        this.sending = false;
        this.showConfirm = false;
      }
    },
    async loadAreas() {
      try {
        this.areas = (await api.get('/v2/email-broadcasts/areas')).data;
      } catch (error) {
        console.error('failed to load areas', error);
      }
    },
    async loadTemplates() {
      try {
        this.templates = (await api.get('/v2/email-templates')).data;
      } catch (error) {
        console.error('failed to load email templates', error);
      }
    },
    async loadBroadcasts() {
      this.loadingBroadcasts = true;
      try {
        this.broadcasts = (await api.get('/v2/email-broadcasts')).data;
      } catch (error) {
        console.error('failed to load saved emails', error);
      } finally {
        this.loadingBroadcasts = false;
      }
    },
    // drafts and copies only: a sent email stays as the record of who it went to
    async remove(broadcast) {
      if (!confirm(`Delete "${broadcast.subject || '(no subject)'}"? This can't be undone.`)) {
        return;
      }
      try {
        await api.delete(`/v2/email-broadcasts/${broadcast.id}`);
      } catch (error) {
        alert(this.errorText(error, 'Failed to delete'));
        return;
      }
      if (broadcast.id === this.broadcastId) {
        // nothing left to lose, so skip the unsaved-changes question
        this.showEmail(null);
        this.$router.push({query: {...this.$route.query, id: undefined}});
      } else {
        this.loadBroadcasts();
      }
    },
    // queued but not all sent yet: scheduled for later, or part way through
    isCancellable(broadcast) {
      return broadcast.status === 'scheduled' || broadcast.status === 'sending' || this.isApplyingCoupon(broadcast);
    },
    isApplyingCoupon(broadcast) {
      return broadcast.status === 'applying coupon';
    },
    // deletes the email's jobs still in the queue; it stays as cancelled, copy it to send again
    async cancel(broadcast) {
      const sent = broadcast.recipientCount - broadcast.remaining;
      const couponNote = broadcast.coupon ? ' Coupons already added stay on the accounts.' : '';
      const question = this.isApplyingCoupon(broadcast)
        ? `Stop adding the coupon and cancel "${broadcast.subject}"? No emails have gone out.`
        : sent === 0
        ? `Cancel "${broadcast.subject}"? None of its ${broadcast.remaining} emails have been sent, and none will be.`
        : `Cancel "${broadcast.subject}"? ${sent} have been sent; the other ${broadcast.remaining} will not go out.`;
      if (!confirm(`${question}${couponNote} To send it later, copy it.`)) {
        return;
      }
      try {
        await api.post(`/v2/email-broadcasts/${broadcast.id}/cancel`);
      } catch (error) {
        alert(this.errorText(error, 'Failed to cancel'));
      }
      this.loadBroadcasts();
      if (broadcast.id === this.broadcastId) {
        this.loadEmail(this.broadcastId);
      }
    },
  },
};
</script>

<style scoped>
.preview-card {
  position: sticky;
  top: 72px;
}

.preview-container {
  display: flex;
  justify-content: center;
  background: #f5f5f5;
  max-height: calc(100vh - 200px);
  overflow: auto;
}

.preview-container iframe {
  width: 100%;
  min-height: 400px;
  background: white;
}

.desktop-viewport {
  width: 100%;
}

.mobile-viewport {
  width: 375px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

::v-deep .clickable-rows tbody tr {
  cursor: pointer;
}
</style>
